import { BadgeColor, BadgeVariant } from '@/shared/design-system';

import { DerivedInvestorStatus } from './types';

/**
 * Whose move it is. The badge treatment is chosen from this rather than from
 * the status itself, so a step the investor has to act on never renders like a
 * step the fund is working through — four statuses used to share the info
 * palette, leaving "waiting on you" and "waiting on them" indistinguishable.
 */
export type InvestorStatusTone = 'action' | 'waiting' | 'settled' | 'closed';

export const STATUS_TONE: Record<DerivedInvestorStatus, InvestorStatusTone> = {
  'investor eligibility': 'action',
  'sign agreement': 'action',
  'data acknowledgment': 'action',
  'awaiting payment': 'action',
  'wire in transit': 'waiting',
  'funds sent': 'waiting',
  'pending units': 'waiting',
  completed: 'settled',
  rejected: 'closed',
  'not eligible': 'closed',
  expired: 'closed',
  redeemed: 'closed',
};

/**
 * Investor-facing label per derived status. The four in-wizard steps borrow the
 * wizard's own step names (Amount · Eligibility · Sign & Consent · Payment) so
 * the badge, the progress timeline and the wizard agree on what the step is
 * called.
 *
 * `rejected` reads as a withdrawal because that is the only thing it can be: on
 * the real path the stage is reached solely through `SubscriptionStatus.Cancelled`,
 * which the backend accepts only from the subscription's own investor. A fund
 * declining an investor lands on `not eligible` instead.
 */
export const STATUS_LABEL: Record<DerivedInvestorStatus, string> = {
  'investor eligibility': 'Eligibility',
  'sign agreement': 'Sign agreement',
  'data acknowledgment': 'Consent',
  'awaiting payment': 'Payment',
  'wire in transit': 'Wire in transit',
  'funds sent': 'Funds sent',
  'pending units': 'Issuing units',
  completed: 'Completed',
  rejected: 'Withdrawn',
  'not eligible': 'Not eligible',
  expired: 'Expired',
  redeemed: 'Redeemed',
};

/**
 * Badge color per derived investor-facing status — shared by the Pending
 * Assets table (`src/pages/portfolio/modules/pending-assets/pending-assets.tsx`) and the
 * "Finalize this investment" modal (ticket 06) so both render the same
 * status the same way. One family per tone, so the palette carries the same
 * meaning the tone does; `not eligible` is the one exception, kept red because
 * it is the fund closing the door rather than an ordinary ending.
 */
export const STATUS_BADGE_COLOR: Record<DerivedInvestorStatus, BadgeColor> = {
  'investor eligibility': 'orange',
  'sign agreement': 'orange',
  'data acknowledgment': 'orange',
  'awaiting payment': 'orange',
  'wire in transit': 'secondary',
  'funds sent': 'secondary',
  'pending units': 'secondary',
  completed: 'primary',
  rejected: 'default',
  'not eligible': 'destructive',
  expired: 'default',
  redeemed: 'default',
};

export const STATUS_BADGE_VARIANT: Record<InvestorStatusTone, BadgeVariant> = {
  action: 'solid',
  waiting: 'subtle',
  settled: 'subtle',
  closed: 'subtle',
};

/**
 * Tooltip copy shown on the status chip. Every status carries one: a badge the
 * investor cannot act on still has to say who is holding the investment and
 * what happens next.
 */
export const STATUS_TOOLTIP: Record<DerivedInvestorStatus, string> = {
  'investor eligibility':
    'Add your investor eligibility details and submit them to the fund to continue.',
  'sign agreement': 'Review and sign the fund documents.',
  'data acknowledgment': 'Confirm the consent items so your details can be shared with the fund.',
  'awaiting payment': 'Choose a payment method and send your funds.',
  'wire in transit':
    "Your wire is on its way. We'll let you know once the fund confirms it arrived.",
  'funds sent': 'Your payment has settled. The fund confirms receipt next.',
  'pending units': 'The fund has your payment and is issuing your units.',
  completed: 'Your units have been issued. This investment is in your portfolio.',
  rejected: 'You withdrew from this subscription. You can start a new one any time.',
  'not eligible': 'The fund could not accept this subscription on eligibility grounds.',
  expired: 'This subscription expired before it was completed.',
  redeemed: 'This investment has been redeemed.',
};
